import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { useToast } from "../hooks/use-toast";
import { AlertTriangle, NotebookPen, Flame, Calendar } from "lucide-react";
import ShareBox from "../components/ShareBox";
import { STATIC_COURSE, TOTAL_WEEKS } from "../content/course_static";
import WeekChecklist from "../components/WeekChecklist";
import { getDisplayForHome, getWeekProgress, toggleTodayForActiveWeek } from "../state/tasks";
import { loadStreak, startStreak, endStreak, computeCompletedDays } from "../state/streak";
import { loadProgress, setCurrentWeek, skipWeek } from "../state/progress";
import "../styles/hud.css";
import { WARNING_SKIP } from "../mock/mock";
import GuidedTour from "../components/tour/GuidedTour";
import { HOME_TOUR_STEPS } from "../tour/steps_home";
import DiaryUnlockOverlay, { DIARY_UNLOCK_KEY } from "../components/DiaryUnlockOverlay";
import { isDiaryAvailable } from "../lib/availability";
import { incrementCounter, getCourseWeekOverrides } from "../lib/api";
import { hasIncrementedThisSession, markIncrementedThisSession } from "../lib/counters";
import { isAnalyticsAllowed, getVisitorId, hasAnalyticsChoice } from "../lib/consent";

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";

// Feature flag: toggle to false to immediately revert to the original gradient-only hero
const USE_HERO_BG_IMAGE = true;
// Background image URLs
const HERO_BG_URL = "https://customer-assets.emergentagent.com/job_recovery-tracker-3/artifacts/wrc9bp2t_Camino%20hacia%20el%20atardecer.png"; // desktop/tablet
const HERO_BG_URL_MOBILE = "https://customer-assets.emergentagent.com/job_recovery-tracker-3/artifacts/2lsjp7bh_Coastal%20Path%20at%20Sunset.png"; // mobile only

function buildOverridesMap(items) {
  const map = {};
  for (const item of Array.isArray(items) ? items : []) {
    const week = Number(item?.week);
    if (!week) continue;
    map[week] = {
      title: typeof item?.title === "string" ? item.title : "",
      task: typeof item?.task === "string" ? item.task : "",
      body: Array.isArray(item?.body) ? item.body : [],
    };
  }
  return map;
}

function withWeekOverride(baseWeek, weekOverride) {
  if (!baseWeek) return null;
  if (!weekOverride) return baseWeek;
  const hasBody = Array.isArray(weekOverride.body) && weekOverride.body.length > 0;
  return {
    ...baseWeek,
    title: weekOverride.title?.trim() ? weekOverride.title : baseWeek.title,
    task: weekOverride.task?.trim() ? weekOverride.task : baseWeek.task,
    body: hasBody ? weekOverride.body : baseWeek.body,
  };
}

export function Home() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [progress, setProgress] = useState(loadProgress());
  const [todayTask, setTodayTask] = useState(() => getDisplayForHome());
  const [tasksStateVersion, setTasksStateVersion] = useState(0);
  const [streak, setStreak] = useState(loadStreak());
  const [nowTick, setNowTick] = useState(Date.now());
  const [weekOverridesMap, setWeekOverridesMap] = useState({});
  const todayChecked = Boolean(todayTask?.checks?.[(todayTask?.dayIdx || 1) - 1]);
  const [todayCardDone, setTodayCardDone] = useState(todayChecked);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        const data = await getCourseWeekOverrides();
        if (!mounted) return;
        setWeekOverridesMap(buildOverridesMap(data?.items));
      } catch (_) {}
    };
    run();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => { const id = setInterval(() => setNowTick(Date.now()), 60000); return () => clearInterval(id); }, []);

  const currentDays = useMemo(() => computeCompletedDays(streak.currentStartAt, nowTick), [streak, nowTick]);

  const HERO_KEY = "nofap27_hero_started_v1";
  const [heroStarted, setHeroStarted] = useState(() => { try { return localStorage.getItem(HERO_KEY) === "1"; } catch { return false; } });
  const continueJourney = () => { const p = loadProgress(); if (p.currentWeek === 0) nav("/week/1"); else nav(`/week/${p.currentWeek}`); };
  const onHeroClick = () => { if (!heroStarted) { try { localStorage.setItem(HERO_KEY, "1"); } catch (e) { /* ignore */ } setHeroStarted(true); nav("/intro"); } else { continueJourney(); } };

  // Count: Total Users to Site (unique per visitor; call only once per session when consented)
  useEffect(() => {
    const key = "total_site";
    const tryIncrement = () => {
      if (hasIncrementedThisSession(key)) return;
      if (!isAnalyticsAllowed()) return;
      const uid = getVisitorId();
      if (!uid) return;
      markIncrementedThisSession(key);
      incrementCounter(key, uid);
    };
    tryIncrement();
    const onConsent = () => tryIncrement();
    window.addEventListener("rl_analytics_consent", onConsent);
    return () => window.removeEventListener("rl_analytics_consent", onConsent);
  }, []);

  useEffect(() => { setTodayTask(getDisplayForHome()); }, [tasksStateVersion]);
  useEffect(() => {
    setTodayCardDone(todayChecked);
  }, [todayChecked]);
  useEffect(() => {
    const onChanged = () => setTasksStateVersion((v) => v + 1);
    window.addEventListener("rl_tasks_changed", onChanged);
    return () => window.removeEventListener("rl_tasks_changed", onChanged);
  }, []);

  const onStartStreak = () => { const updated = startStreak(); setStreak(updated); toast({ title: "Streak started", description: "Tracking from now. Day count shows completed days." }); };
  const onEndStreak = () => { if (!streak.currentStartAt) return; const days = computeCompletedDays(streak.currentStartAt, Date.now()); const ok = window.confirm(`End streak at ${days} completed day${days === 1 ? "" : "s"}?`); if (!ok) return; const updated = endStreak(); setStreak(updated); toast({ title: "Streak ended", description: `Saved to history (${days} day${days === 1 ? "" : "s"}).` }); };

  const startedAtStr = streak.currentStartAt ? new Date(streak.currentStartAt).toLocaleDateString() : null;

  const heroSectionClass = `${USE_HERO_BG_IMAGE ? "hero-image-bg hero-double-height-desktop" : "brand-gradient"} with-shapes elevated-card rounded-2xl p-6 sm:p-8 flex flex-col justify-end ring-1 ring-inset ring-slate-200`;
  const heroStyle = USE_HERO_BG_IMAGE ? { "--hero-bg": `url("${HERO_BG_URL}")`, "--hero-bg-mobile": `url("${HERO_BG_URL_MOBILE}")` } : undefined;

  // Sequence: consent decision first -> diary unlock -> tutorial
  const [consentResolved, setConsentResolved] = useState(() => hasAnalyticsChoice());
  const [showDiaryUnlock, setShowDiaryUnlock] = useState(() => {
    if (!hasAnalyticsChoice()) return false;
    try {
      const seen = localStorage.getItem(DIARY_UNLOCK_KEY) === "1";
      return !seen && isDiaryAvailable();
    } catch { return false; }
  });

  useEffect(() => {
    const onConsent = () => {
      setConsentResolved(hasAnalyticsChoice());
    };
    window.addEventListener("rl_analytics_consent", onConsent);
    return () => window.removeEventListener("rl_analytics_consent", onConsent);
  }, []);

  useEffect(() => {
    if (!consentResolved) {
      setShowDiaryUnlock(false);
      return;
    }
    try {
      const seen = localStorage.getItem(DIARY_UNLOCK_KEY) === "1";
      setShowDiaryUnlock(!seen && isDiaryAvailable());
    } catch {
      setShowDiaryUnlock(false);
    }
  }, [consentResolved]);

  const onCloseDiaryUnlock = (action) => {
    setShowDiaryUnlock(false);
    if (action === "open") nav("/diary");
  };

  return (
    <div data-tour-id="home-root" className="mx-auto max-w-6xl px-4 py-6 sm:py-10">
      {consentResolved && showDiaryUnlock && <DiaryUnlockOverlay onClose={onCloseDiaryUnlock} />}

      <section data-tour-id="hero-cta" className={heroSectionClass} style={heroStyle}>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight sm:leading-[1.15] text-slate-100 hero-text-shadow">Your Journey to Self-Control</h1>
        <p className="mt-4 sm:mt-4 max-w-xl text-base leading-6 sm:text-lg sm:leading-7 text-slate-300 hero-text-shadow hero-subtitle-shadow">{heroStarted ? (
            <>
              Continue where you left off. Your progress{' '}<span className="hidden md:inline"><br /></span>stays on this device.
            </>
          ) : (
            <>
              Read the full Intro first, then begin at Week 1. Your progress{' '}<span className="hidden md:inline"><br /></span>stays on this device.
            </>
          )}</p>
        <div className="mt-4 sm:mt-4">
          <Button onClick={onHeroClick} className="btn-primary focus-brand hero-text-shadow w-full sm:w-auto">{heroStarted ? "Continue Progress" : "Open Intro"}</Button>
        </div>
      </section>

      {/* Streak tracker card */}
      <section className="mt-4 sm:mt-6" data-tour-id="streak-card">
        <Card className="elevated-card with-shapes brand-gradient">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100"><Flame className="h-5 w-5" style={{ color: "var(--brand-700)" }} /> Current Streak</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <div className="text-[42px] sm:text-6xl font-black leading-none brand-text-gradient">{streak.currentStartAt ? currentDays : 0}</div>
                <div className="mt-1 text-slate-600 dark:text-slate-300">{streak.currentStartAt ? (<span className="inline-flex items-center gap-1 text-sm"><Calendar className="h-4 w-4" /> Active since {startedAtStr}</span>) : (<span className="text-sm">No active streak</span>)}</div>
              </div>
            </div>
            <div className="mt-5 grid gap-2 max-w-[360px]">{!streak.currentStartAt ? (<Button onClick={onStartStreak} className="btn-primary">Start Today</Button>) : (<Button variant="destructive" onClick={onEndStreak}>End Streak</Button>)}</div>
          </CardContent>
        </Card>
      </section>

      {/* Minimal Today’s Task */}
      <section className="mt-6 sm:mt-10">
        <div className="mx-auto max-w-6xl px-4">
          <div className="w-full md:w-1/2 lg:w-5/12 xl:w-4/12 max-w-md">
            <div className="flex flex-col">
              <div className="flex-1 min-w-0">
                <Card
                  data-tour-id="today-task"
                  data-testid="home-today-task-card"
                  className={`shadow-sm border transition-all duration-200 ${
                    todayCardDone
                      ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 shadow-[0_0_0_1px_rgba(16,185,129,0.35),0_10px_24px_rgba(16,185,129,0.16)]"
                      : "border-emerald-300/60 dark:border-emerald-700/50 shadow-[0_0_0_1px_rgba(16,185,129,0.18),0_8px_20px_rgba(16,185,129,0.10)]"
                  }`}
                >
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center gap-2 text-slate-900 dark:text-slate-100"><NotebookPen className="h-4 w-4" /> Today’s Task</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 pb-4">
                    {(() => {
                      const activeWeek = todayTask.week;
                      const day = todayTask.dayIdx;
                      const baseContent = STATIC_COURSE.weeks.find((w) => w.week === activeWeek);
                      const content = withWeekOverride(baseContent, weekOverridesMap[activeWeek]);
                      const taskText = content?.task || content?.title || "Task";
                      const label = `Week ${activeWeek} — Day ${day}`;
                      const checked = todayTask.checks[day - 1];

                      const onChange = async (e) => {
                        const next = e.target.checked;
                        setTodayCardDone(next);
                        const res = toggleTodayForActiveWeek(next);
                        setTasksStateVersion((v) => v + 1);
                        setProgress(loadProgress());
                        if (res.toggledOn) {
                          if (res.weekCompleted) toast({ title: `Week ${activeWeek} completed`, description: "Next week unlocks tomorrow." });
                          else toast({ title: "Good job", description: `Completed today’s task (${label}).` });
                        } else if (res.toggledOff) {
                          toast({ title: "Undone", description: "Reverted today’s completion." });
                        }
                      };

                      return (
                        <div>
                          <div className="flex items-start gap-3">
                            <input id="today-check" type="checkbox" className="h-4 w-4 mt-0.5 accent-emerald-600" onChange={onChange} checked={checked} data-testid="home-today-task-checkbox" />
                            <label htmlFor="today-check" className="text-sm text-slate-800 dark:text-slate-100">
                              <div className="font-medium">{label}</div>
                              <div className="text-slate-600 dark:text-slate-300 text-xs">{taskText}</div>
                              <div className="mt-2 text-[11px] text-red-600 dark:text-red-400">If any day is missed, the week resets.</div>
                              {todayTask.blockedUntilTomorrow && (<div className="mt-1 text-[11px] text-slate-500">Finished today. Next week appears tomorrow.</div>)}
                            </label>
                          </div>
                          {todayTask.justReset && (<div className="mt-2 text-[11px] text-red-600 dark:text-red-400">Week reset today due to a missed day.</div>)}
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              </div>
              <div className="mt-4 lg:mt-0 lg:w-1/4"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom share box */}
      <div className="mx-auto max-w-6xl px-4"><div className="mt-6"><ShareBox variant="home" /></div></div>

      {/* Guided tour: only after consent decision and diary unlock sequence */}
      <GuidedTour steps={HOME_TOUR_STEPS} storageKey="rl_tour_completed_v1" autoStart={consentResolved && !showDiaryUnlock} startEvent="rl_tour_start_home" />
    </div>
  );
}

export function Intro() {
  const nav = useNavigate();
  const paras = STATIC_COURSE.intro.paragraphs;

  // Count: Intro enters once per session (unique per visitor via backend)
  useEffect(() => {
    const key = "intro";
    if (!hasIncrementedThisSession(key)) {
      markIncrementedThisSession(key);
      if (isAnalyticsAllowed()) {
        const uid = getVisitorId();
        if (uid) incrementCounter(key, uid);
      }
    }
  }, []);

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:py-10">
      <Card className="elevated-card">
        <CardHeader>
          <CardTitle className="text-slate-900 dark:text-slate-100">{STATIC_COURSE.intro.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-slate-700 dark:text-slate-300">{paras.map((p, i) => (<p key={i}>{p}</p>))}</div>
          <div className="mt-6 flex justify-end"><Button className="btn-primary" onClick={() => nav("/week/1")}>Go to Week 1</Button></div>
        </CardContent>
      </Card>
    </div>
  );
}

export function Week() {
  const nav = useNavigate();
  const { toast } = useToast();
  const { weekNumber } = useParams();
  const week = Number(weekNumber);
  const [showWarning, setShowWarning] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [tasksTick, setTasksTick] = useState(0);
  const [weekOverridesMap, setWeekOverridesMap] = useState({});
  useEffect(() => {
    const onChanged = () => setTasksTick((v) => v + 1);
    window.addEventListener("rl_tasks_changed", onChanged);
    return () => window.removeEventListener("rl_tasks_changed", onChanged);
  }, []);

  useEffect(() => { setCurrentWeek(week); }, [week]);
  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        const data = await getCourseWeekOverrides();
        if (!mounted) return;
        setWeekOverridesMap(buildOverridesMap(data?.items));
      } catch (_) {}
    };
    run();
    return () => {
      mounted = false;
    };
  }, []);
  useEffect(() => { setShowWarning(false); }, [week]);
  useEffect(() => { try { window.scrollTo({ top: 0, behavior: 'auto' }); } catch (_) { /* ignore */ } }, [week]);

  // Count: Week N enters once per session per week (unique enforced by backend)
  useEffect(() => {
    if (!Number.isFinite(week) || week <= 0) return;
    const key = `week_${week}`;
    if (!hasIncrementedThisSession(key)) {
      markIncrementedThisSession(key);
      if (isAnalyticsAllowed()) {
        const uid = getVisitorId();
        if (uid) incrementCounter(key, uid);
      }
    }
  }, [week]);

  const baseContent = STATIC_COURSE.weeks.find((w) => w.week === week);
  const content = withWeekOverride(baseContent, weekOverridesMap[week]);
  const weeklyTask = content?.task || content?.title || "Task";

  if (!content) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-6 sm:py-10">
        <Card className="elevated-card"><CardHeader><CardTitle>Week not found</CardTitle></CardHeader><CardContent><Button onClick={() => nav("/tracker")} variant="secondary">Go to Tracker</Button></CardContent></Card>
      </div>
    );
  }

  const onComplete = () => { toast({ title: "Completion is automatic", description: "Finish all 7 daily tasks to complete the week." }); };
  const onSkip = () => setShowWarning(true);
  const confirmSkip = () => { setSkipping(true); setTimeout(() => { skipWeek(week); if (week < TOTAL_WEEKS) { window.scrollTo({ top: 0, behavior: 'auto' }); nav(`/week/${week + 1}`); setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 0); } else { nav("/tracker"); } }, 350); };

  const wp = getWeekProgress(week);
  const onDayToggle = (d, next, meta) => {
    if (!meta.current) {
      toast({ title: `Day ${d} is locked`, description: "Only today’s task can be toggled." });
      return;
    }
    const res = toggleTodayForActiveWeek(next);
    setTasksTick((v) => v + 1);
    if (res.toggledOn) {
      if (res.weekCompleted) toast({ title: `Week ${week} completed`, description: "Next week unlocks tomorrow." });
      else toast({ title: "Good job", description: `Completed today’s task (Week ${week} — Day ${wp.dayIdx}).` });
    } else if (res.toggledOff) {
      toast({ title: "Undone", description: "Reverted today’s completion." });
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:py-10">
      <Card className="elevated-card">
        <CardHeader>
          <CardTitle className="text-slate-900 dark:text-slate-100">{content.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-slate-700 dark:text-slate-300 weekly-info-font">{content.body.map((para, i) => (<p key={i}>{para}</p>))}</div>

          <div className="mt-8">
            <div className="mb-6 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/50 p-4 sm:p-5">
              <div className="text-[11px] sm:text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Weekly task</div>
              <div className="mt-1 text-sm sm:text-base leading-relaxed text-slate-800 dark:text-slate-100 break-words">{weeklyTask}</div>
            </div>
            <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">This week’s daily progress</div>
            <WeekChecklist dayIdx={wp.dayIdx} checks={wp.checks} onDayToggle={onDayToggle} toastLocked={(d) => toast({ title: `Day ${d} is locked`, description: "Only today’s task can be toggled." })} />
            <div className="mt-2 text-xs text-slate-500">{wp.lastCompletedAt ? `Last completion: ${wp.lastCompletedAt}` : "No daily tasks completed yet."}</div>
            <div className="mt-3 text-sm text-red-600 dark:text-red-400">If any day among the past days is not completed, the week resets.</div>
          </div>

          {showWarning && (
            <div className="mt-5">
              <Alert variant="destructive"><AlertTriangle className="h-5 w-5" /><AlertTitle>Careful</AlertTitle><AlertDescription> {WARNING_SKIP} </AlertDescription></Alert>
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <Button variant="destructive" onClick={confirmSkip} disabled={skipping}>{skipping ? "Skipping..." : "Confirm Skip"}</Button>
                <Button variant="secondary" onClick={() => setShowWarning(false)} disabled={skipping}>Cancel</Button>
              </div>
            </div>
          )}

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <Button className="btn-primary focus-brand" onClick={onComplete}>Mark Week Complete</Button>
            {(week === 0 || week === 1) ? (<Button variant="outline" onClick={onSkip}>Skip Week</Button>) : (<Button variant="outline" disabled title="Skipping disabled for this week">Skip Week</Button>)}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function Tracker() {
  const MOD_KEY = "rl_tracker_unlocked_weeks_v1";
  const [moderatorOpen, setModeratorOpen] = React.useState(false);
  const [pwd, setPwd] = React.useState("");
  const [unlockedWeeks, setUnlockedWeeks] = React.useState(() => {
    try {
      const raw = localStorage.getItem(MOD_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [pendingWeek, setPendingWeek] = React.useState(null);
  const nav = useNavigate();
  const progress = loadProgress();
  const WEEKS_LEN = TOTAL_WEEKS;

  const canOpen = (w) => {
    if (w === 0) return true;
    if (unlockedWeeks.includes(w)) return true; // moderator-unlocked specific week
    if (progress.completedWeeks.includes(w) || (progress.skippedWeeks || []).includes(w)) return true;
    return progress.currentWeek === w;
  };

  const onLockedClick = (w) => {
    setPendingWeek(w);
    setModeratorOpen(true);
  };

  const submitPwd = () => {
    if (pwd === "moderatorJ" && pendingWeek) {
      try {
        const next = Array.from(new Set([...(unlockedWeeks || []), pendingWeek])).sort((a,b)=>a-b);
        localStorage.setItem(MOD_KEY, JSON.stringify(next));
        setUnlockedWeeks(next);
      } catch (e) { /* ignore */ }
      setModeratorOpen(false);
      setPwd("");
      setPendingWeek(null);
    }
  };

  const statusOf = (w) => progress.completedWeeks.includes(w) ? "completed" : (progress.skippedWeeks || []).includes(w) ? "skipped" : progress.currentWeek === w ? "current" : "pending";
  const goWeek = (w) => { if (w === 0) nav("/intro"); else nav(`/week/${w}`); };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:py-10">
      <div className="mb-6 brand-gradient with-shapes elevated-card rounded-xl p-6 ring-1 ring-inset ring-slate-200"><h2 className="text-xl sm:text-2xl font-semibold text-slate-900 dark:text-slate-100">Progress Tracker</h2><p className="mt-1 text-slate-600 dark:text-slate-300">Your journey at a glance.</p></div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {Array.from({ length: WEEKS_LEN }, (_, i) => {
          const w = i + 1;
          const locked = !canOpen(w) && progress.currentWeek !== w;
          const st = statusOf(w);
          const cls = st === "completed" ? "tile-completed" : st === "skipped" ? "tile-skipped" : st === "current" ? "tile-current" : "tile-pending";
          const onClick = () => {
            if (locked) {
              onLockedClick(w);
            } else {
              goWeek(w);
            }
          };
          return (
            <button key={w} className={`tile-base ${cls}`} onClick={onClick} style={locked ? { opacity: 0.6 } : undefined}>
              Week {w}
            </button>
          );
        })}
      </div>

      <Dialog open={moderatorOpen} onOpenChange={setModeratorOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enter moderator password</DialogTitle>
            <DialogDescription>Unlocks only the selected week on the Tracker. Daily tasks still follow the original timing path. Reset Progress will remove these unlocks.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <Input type="password" placeholder="Password" value={pwd} onChange={(e) => setPwd(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") submitPwd(); }} />
            <div className="flex gap-2 justify-end">
              <button className="px-3 py-1.5 text-sm rounded border" onClick={() => setModeratorOpen(false)}>Cancel</button>
              <button className="px-3 py-1.5 text-sm rounded bg-slate-900 text-white" onClick={submitPwd} disabled={!pwd}>Unlock</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
